import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Admin block: connect Workspace mailboxes (user-authorized Gmail API, gmail.send only), Podio status, test email.
export default function MailConnectPanel() {
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState(null);
  const [params] = useSearchParams();
  const load = () => {
    api.get("/admin/mail/accounts").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/admin/integrations/status").then((r) => setStatus(r.data)).catch(() => {});
  };
  useEffect(load, []);
  useEffect(() => {
    if (params.get("mail") === "connected") toast.success(`Mailbox connected: ${params.get("mailbox")}`);
    if (params.get("mail") === "error") toast.error(`Mailbox connection failed: ${params.get("reason")}`);
  }, [params]);

  const connect = async (mailbox) => {
    try { const r = await api.get("/admin/mail/connect", { params: { mailbox } }); window.location.href = r.data.url; }
    catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not start connection"); }
  };
  const testEmail = async () => {
    try { const r = await api.post("/admin/integrations/test-email"); r.data.sent ? toast.success("Test email accepted by Google. Check the acquisitions inbox.") : toast.error(`Not sent: ${r.data.error}`); }
    catch { toast.error("Test failed"); }
  };
  const disconnect = async (mailbox) => { if (!window.confirm(`Disconnect ${mailbox}?`)) return; await api.delete(`/admin/mail/accounts/${mailbox}`); load(); };

  if (!info) return null;
  const connected = new Set((info.accounts || []).map((a) => a.mailbox));
  return (
    <div className="card-frame p-6 mt-10" data-testid="mail-connect-panel">
      <h2 className="font-display text-xl">Google Workspace email</h2>
      <p className="text-xs text-neutral-500 mt-1">Provider: <span className="text-neutral-300">{info.provider || "not set"}</span> · OAuth client {info.oauth_client_configured ? "configured" : "missing (set GOOGLE_OAUTH_CLIENT_ID / GOOGLE_OAUTH_CLIENT_SECRET in environment secrets)"}</p>
      <p className="text-xs text-neutral-500 mt-1 break-all">Authorized redirect URIs to register in Google Cloud: <span className="text-neutral-300">{info.redirect_uri}</span>{info.production_redirect_uri && info.production_redirect_uri !== info.redirect_uri ? <> and <span className="text-neutral-300">{info.production_redirect_uri}</span></> : null}</p>
      <ul className="mt-4 space-y-2 text-sm">
        {info.needed.map((mb) => (
          <li key={mb} className="flex flex-wrap items-center justify-between gap-2 border border-white/10 px-3 py-2" data-testid={`mailbox-${mb}`}>
            <span>{mb} <span className={`text-[10px] uppercase tracking-wider ml-2 ${connected.has(mb) ? "text-emerald-300" : "text-amber-300"}`}>{connected.has(mb) ? "connected" : "not connected"}</span></span>
            <span className="flex gap-2">
              <button onClick={() => connect(mb)} disabled={!info.oauth_client_configured || info.provider !== "gmail_oauth"} className="btn-ghost px-3 py-1.5 text-xs disabled:opacity-40" data-testid={`connect-${mb}`}>{connected.has(mb) ? "Reconnect" : "Connect mailbox"}</button>
              {connected.has(mb) && <button onClick={() => disconnect(mb)} className="text-xs text-red-400">Disconnect</button>}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-neutral-600 mt-3">Sign in as the mailbox itself when Google asks. Only the "send email on your behalf" permission is requested. If invest@ is an alias of another mailbox, connect that mailbox and tell me so the alias can be recorded.</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <button onClick={testEmail} className="btn-ghost px-3 py-1.5 text-xs" data-testid="send-test-email">Send test email to acquisitions@</button>
      </div>
      {status && (
        <div className="mt-6 text-xs text-neutral-400 space-y-1" data-testid="podio-status">
          <div className="overline mb-2">Podio</div>
          <div>{status.podio.configured ? (status.podio.ok ? `Connected to app "${status.podio.app_name}" (${Object.keys(status.podio.fields || {}).length} fields found)` : `Configured but failing: ${status.podio.error}`) : "Not configured (PODIO_CLIENT_ID, PODIO_CLIENT_SECRET, PODIO_APP_ID, PODIO_APP_TOKEN)"}</div>
          <div className="overline mt-4 mb-2">Routing</div>
          <div>Sell / Buyers / Contact → {status.routing.seller} · Investors → {status.routing.investor}</div>
        </div>
      )}
    </div>
  );
}
